import React from 'react';
import fhirpath from 'fhirpath';

import { PassoExecutado } from '@/models/PassoExecutado';

import { ArtifactFormOverrides } from '@/features/artifacts/components/ArtifactForm/ArtifactForm.types';
import { useArtifactFormConfig } from '@/features/artifacts/hooks/useArtifactFormConfig';

import Markdown from '@/components/Markdown';
import { clinicalDocumentPdf } from '@/features/pdf/documents/ClinicalDocumentLayout';
import DocumentPageOrientacao from '@/features/pdf/documents/DocumentPageOrientacao';
import { useAuth } from '@/features/auth/providers/AuthProvider/hooks';
import { useEnterpriseContext } from '@/features/enterprise/contexts/EnterpriseContext';
import Feature from '@/features/features/components/Feature';
import IconButton from '@/components/IconButton';
import { FiPrinter } from 'react-icons/fi';
import SolicitacaoList from '..';
import CardSolicitacao, {
  CardSolicitacaoProxyProps,
} from '../../CardSolicitacao';

const Form: ArtifactFormOverrides = [
  {
    name: 'narrative',
    label: 'Orientação médica',
    required: true,
    containerStyle: {
      flex: '0 1 100%',
    },
  },
];

const CardOrientacoesMedicas: React.FC<CardSolicitacaoProxyProps> = ({
  solicitacao,
  ...rest
}) => {
  const form = useArtifactFormConfig('orientacoes-medicas', Form);
  const { user } = useAuth();
  const [{ encounter }] = useEnterpriseContext();

  const [printing, setPrinting] = React.useState(false);

  const title = React.useMemo(() => {
    return (
      fhirpath.evaluate(solicitacao.pedido, 'CarePlan.title')[0] ||
      'Orientação médica'
    );
  }, [solicitacao.pedido]);

  const renderDescription = React.useCallback(() => {
    const narrative = solicitacao.pedido.text?.div;
    return <Markdown text={narrative || ''} />;
  }, [solicitacao.pedido]);

  const print = React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      try {
        setPrinting(true);

        await clinicalDocumentPdf(DocumentPageOrientacao, {
          user,
          resource: solicitacao.pedido as fhir4.CarePlan,
          encounter: encounter.data,
        });
      } catch (error) {
        throw new Error(`Erro ao gerar PDF da orientação médica: ${error}`);
      } finally {
        setPrinting(false);
      }
    },
    [user, solicitacao.pedido, encounter.data],
  );

  const renderPrintSection = React.useCallback(() => {
    return (
      <Feature name="solicitacoes.orientacoes_medicas.can_print">
        <IconButton
          disabled={printing}
          tooltip="Imprimir"
          onClick={print}
          icon={<FiPrinter />}
        />
      </Feature>
    );
  }, [print, printing]);

  return (
    <CardSolicitacao
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
      title={title}
      description={renderDescription}
      solicitacao={solicitacao}
      form={form}
      renderActions={renderPrintSection}
    />
  );
};

interface Props {
  passo: PassoExecutado;
}

const SolicitacaoListOrientacoesMedicas: React.FC<Props> = ({ passo }) => {
  return (
    <SolicitacaoList
      passo={passo}
      tipo="orientacoes-medicas"
      CardSolicitacaoComponent={CardOrientacoesMedicas}
      groupTitle="Selecione as orientações médicas que você quer solicitar no seu prontuário."
      addConfig={{
        title: 'Adicionar orientação médica complementar',
      }}
    />
  );
};

export default SolicitacaoListOrientacoesMedicas;
